/**
 * Channels Mutation Services
 * Contains all write operations (mutations) for channels
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  ChannelType,
  ChannelName,
  IApiResponse,
  IConnectAirbnbResponse,
  IImportListingsRequest,
  IImportListingsResponse,
  ICheckBookingConnectionRequest,
  ICheckBookingConnectionResponse,
  IValidateBookingHotelIdsRequest,
  IValidateBookingHotelIdsResponse,
  IUpdateListingStatusRequest,
  IUpdateListingStatusResponse,
  IGathernRequestOTPRequest,
  IGathernRequestOTPResponse,
  IGathernSendOTPApiResponse,
  IGathernVerifyOTPRequest,
  IGathernVerifyOTPResponse,
  IGathernVerifyOTPApiResponse,
  IDisconnectAccountRequest,
  IDisconnectAccountResponse,
} from '../types';
import { mapChannelNameToType } from './channels.service';

// ============================================
// API Endpoints
// ============================================

const API_ENDPOINTS = {
  CONNECT_AIRBNB: '/api/DistributionManagement/ConnectAirbnb',
  IMPORT_LISTINGS: '/api/DistributionManagement/ImportListings',
  GATHERN_SEND_OTP: '/api/DistributionManagement/GathernSendOtp',
  GATHERN_VERIFY_OTP: '/api/DistributionManagement/GathernVerifyOtp',
  CHECK_BOOKING_CONNECTION: '/api/DistributionManagement/CheckBookingConnection',
  VALIDATE_BOOKING_HOTEL_IDS: '/api/DistributionManagement/ValidateBookingHotelIds',
  UPDATE_LISTING_STATUS: '/api/DistributionManagement/UpdateListingStatus',
  DISCONNECT_ACCOUNT: '/api/DistributionManagement/DisconnectAccount',
  RESYNC_ACCOUNT: '/api/DistributionManagement/ResyncAccount',
} as const;

// ============================================
// Airbnb Connection
// ============================================

/**
 * Hook to connect to Airbnb channel
 * @returns Mutation for connecting to Airbnb
 */
export const useConnectAirbnbMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const connectAirbnb = async (): Promise<IConnectAirbnbResponse> => {
    const response = await apiPrivate.post<IApiResponse<IConnectAirbnbResponse>>(
      API_ENDPOINTS.CONNECT_AIRBNB
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to connect to Airbnb'
      );
    }

    const result = response.data.result;
    if (!result.success) {
      const error = new Error(result.errorMessage || 'Failed to connect to Airbnb') as Error & {
        errorMessageAr?: string;
      };
      error.errorMessageAr = result.errorMessageAr ?? undefined;
      throw error;
    }

    return result;
  };

  return useMutation({
    mutationFn: connectAirbnb,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
};

// ============================================
// Gathern Connection
// ============================================

/**
 * Hook to request OTP for Gathern login
 * @returns Mutation for requesting OTP
 */
export const useGathernRequestOTPMutation = () => {
  const { apiPrivate } = useApi();

  const requestGathernOTP = async (request: IGathernRequestOTPRequest): Promise<IGathernRequestOTPResponse> => {
    let phone = request.phoneNumber;
    if (phone.startsWith('+966')) {
      phone = '0' + phone.slice(4);
    }

    try {
      const response = await apiPrivate.post<IGathernSendOTPApiResponse>(
        `${API_ENDPOINTS.GATHERN_SEND_OTP}?phone=${encodeURIComponent(phone)}`
      );

      if (!response.data.isSuccess) {
        const error = new Error(
          response.data.errorMessages?.join(', ') || 'Failed to send OTP'
        ) as Error & { messageAr?: string };
        error.messageAr = 'فشل في إرسال رمز التحقق';
        throw error;
      }

      const result = response.data.result;
      if (!result?.success) {
        // API returns 'messages' array per Swagger
        const errorMsg = result?.messages?.[0] || 'Failed to send OTP';
        const error = new Error(errorMsg) as Error & { messageAr?: string };
        error.messageAr = 'فشل في إرسال رمز التحقق';
        throw error;
      }

      return {
        success: true,
        message: result.messages?.[0] || 'OTP sent successfully',
        messageAr: 'تم إرسال رمز التحقق بنجاح',
      };
    } catch (err) {
      const error = err as Error & { messageAr?: string };
      if (!error.messageAr) {
        error.messageAr = 'فشل في إرسال رمز التحقق';
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: requestGathernOTP,
  });
};

/**
 * Hook to verify OTP for Gathern login
 * @returns Mutation for verifying OTP
 */
export const useGathernVerifyOTPMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const verifyGathernOTP = async (request: IGathernVerifyOTPRequest): Promise<IGathernVerifyOTPResponse> => {
    let phone = request.phoneNumber;
    if (phone.startsWith('+966')) {
      phone = '0' + phone.slice(4);
    }

    try {
      const response = await apiPrivate.post<IGathernVerifyOTPApiResponse>(
        `${API_ENDPOINTS.GATHERN_VERIFY_OTP}?phone=${encodeURIComponent(phone)}&otp=${encodeURIComponent(request.otp)}`
      );

      if (!response.data.isSuccess) {
        const error = new Error(
          response.data.errorMessages?.join(', ') || 'Failed to verify OTP'
        ) as Error & { messageAr?: string };
        error.messageAr = 'فشل في التحقق من الرمز';
        throw error;
      }

      const result = response.data.result;
      if (!result?.success) {
        const error = new Error(result?.message || 'Failed to verify OTP') as Error & { messageAr?: string };
        error.messageAr = result?.messageAr || 'فشل في التحقق من الرمز';
        throw error;
      }

      return {
        success: true,
        message: result.message || 'OTP verified successfully',
        messageAr: result.messageAr || 'تم التحقق من الرمز بنجاح',
        token: result.token || '',
        channelType: ChannelType.Gathern,
      };
    } catch (err) {
      const error = err as Error & { messageAr?: string };
      if (!error.messageAr) {
        error.messageAr = 'فشل في التحقق من الرمز';
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: verifyGathernOTP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
};

// ============================================
// Booking Connection
// ============================================

/**
 * Hook to check Booking connection
 * @returns Mutation for checking Booking connection
 */
export const useCheckBookingConnectionMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const checkBookingConnection = async (
    request: ICheckBookingConnectionRequest
  ): Promise<ICheckBookingConnectionResponse> => {
    // Build query params as per API specification
    const params = new URLSearchParams();
    params.append('hotelId', request.hotelId);
    params.append('connectionType', request.connectionType.toString());
    if (request.mainChannelId !== undefined) {
      params.append('mainChannelId', request.mainChannelId.toString());
    }

    const response = await apiPrivate.post<IApiResponse<ICheckBookingConnectionResponse>>(
      `${API_ENDPOINTS.CHECK_BOOKING_CONNECTION}?${params.toString()}`
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to check Booking connection'
      );
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: checkBookingConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
};

/**
 * Hook to validate Booking hotel IDs
 * @returns Mutation for validating hotel IDs
 */
export const useValidateBookingHotelIdsMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const validateHotelIds = async (
    request: IValidateBookingHotelIdsRequest
  ): Promise<IValidateBookingHotelIdsResponse> => {
    const response = await apiPrivate.post<IApiResponse<IValidateBookingHotelIdsResponse>>(
      API_ENDPOINTS.VALIDATE_BOOKING_HOTEL_IDS,
      request
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to validate hotel IDs'
      );
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: validateHotelIds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
};

// ============================================
// Import Units
// ============================================

/**
 * Hook to import units from a channel
 * @returns Mutation for importing units
 */
export const useImportUnitsMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const importUnits = async (request: IImportListingsRequest): Promise<IImportListingsResponse> => {
    const response = await apiPrivate.post<IApiResponse<IImportListingsResponse>>(
      API_ENDPOINTS.IMPORT_LISTINGS,
      request
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to import units'
      );
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: importUnits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel-units-import'] });
    },
  });
};

// ============================================
// Update Listing Status
// ============================================

/**
 * Hook to update listing status
 * @returns Mutation for updating listing status
 */
export const useUpdateListingStatusMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const updateListingStatus = async (
    request: IUpdateListingStatusRequest
  ): Promise<IUpdateListingStatusResponse> => {
    const response = await apiPrivate.put<IApiResponse<IUpdateListingStatusResponse>>(
      API_ENDPOINTS.UPDATE_LISTING_STATUS,
      request
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to update listing status'
      );
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: updateListingStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel-units-import'] });
    },
  });
};

// ============================================
// Mock Connect Channel (for testing)
// ============================================

interface IConnectChannelRequest {
  channelName: ChannelName;
  credentials?: {
    email?: string;
    phone?: string;
    hotelId?: string;
  };
}

interface IConnectChannelResponse {
  success: boolean;
  channelId: string;
  message: string;
}

/**
 * Hook to connect to a channel (mock for testing)
 * @returns Mutation for connecting to a channel
 */
export const useConnectChannelMutation = () => {
  const queryClient = useQueryClient();

  const connectChannel = async (request: IConnectChannelRequest): Promise<IConnectChannelResponse> => {
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const shouldFail = Math.random() < 0.3;
    
    if (shouldFail) {
      throw new Error('فشل الاتصال بالقناة. يرجى المحاولة مرة أخرى.');
    }

    const channelId = `${request.channelName}-${Date.now()}`;
    
    localStorage.setItem(`${request.channelName}Connected`, 'true');
    localStorage.setItem(`${request.channelName}ChannelId`, channelId);

    return {
      success: true,
      channelId,
      message: `Successfully connected to ${request.channelName}`,
    };
  };

  return useMutation({
    mutationFn: connectChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
};

// ============================================
// Disconnect Account
// ============================================

/**
 * Hook to disconnect a channel account
 * @returns Mutation for disconnecting an account
 */
export const useDisconnectAccountMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const disconnectAccount = async (request: IDisconnectAccountRequest): Promise<IDisconnectAccountResponse> => {
    const response = await apiPrivate.post<IApiResponse<IDisconnectAccountResponse>>(
      API_ENDPOINTS.DISCONNECT_ACCOUNT,
      null,
      {
        params: {
          userChannelId: request.userChannelId,
        },
      }
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to disconnect account'
      );
    }

    return {
      ...response.data.result,
      success: true,
    };
  };

  return useMutation({
    mutationFn: disconnectAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel'] });
    },
  });
};

// ============================================
// Resync Account
// ============================================

/** Request type for resync account */
export interface IResyncAccountRequest {
  userChannelId: number;
}

/** Response type for resync account */
export interface IResyncAccountResponse {
  success: boolean;
}

/**
 * Hook to resync a disconnected channel account
 * Reactivates the channel and updates all listings to Connected status
 * @returns Mutation for resyncing an account
 */
export const useResyncAccountMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const resyncAccount = async (request: IResyncAccountRequest): Promise<IResyncAccountResponse> => {
    const response = await apiPrivate.post<IApiResponse<unknown>>(
      API_ENDPOINTS.RESYNC_ACCOUNT,
      null,
      {
        params: {
          userChannelId: request.userChannelId,
        },
      }
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to resync account'
      );
    }

    return {
      success: true,
    };
  };

  return useMutation({
    mutationFn: resyncAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel'] });
      queryClient.invalidateQueries({ queryKey: ['channel-by-type'] });
      queryClient.invalidateQueries({ queryKey: ['channel-units-import'] });
    },
  });
};
